// The org chart's edge rules (02 §seat_edge, "Seat invariants"; 05 §Model `seat`, §Delegation
// rules; 04 §Org `PUT org-chart/edges`, `POST simulate`). No source has three edge kinds, so this
// is new code; the cycle rule is KPI Beast's role-tree rule (role-tree.ts: a parent that is
// transitively our own child is refused), applied per edge kind.
//
// `planEdges` is pure: given the company's seats (with whether each has an active person and an
// active digital-employee holder), its stored edges and a `PUT edges` body, it returns what the
// write would change and every reason it would be refused. The API applies the plan only when
// it has no input errors and no problems; `simulate` returns it as is.
//
// Rules:
//   - the body replaces the stored edges of `kinds` (default all three); edges of other kinds
//     stay. Edges touching a `proposed` seat are invisible (02 §seat: hidden from the chart):
//     the body cannot name one (`unknown_seat`) and the stored ones stay as they are;
//   - `reports_to` is `seat.supervisor_seat_id` (the canonical field every reader uses); its
//     rows mirror it. A closed seat's line is frozen: re-sending it is a no-op, moving it is
//     `seat_closed`, leaving it out keeps it. A new edge to or from a closed seat is
//     `seat_closed`; an existing one may stay or go;
//   - acyclic per kind (a self-edge is a cycle of one), at most one `reports_to` per seat;
//   - supervisor rule (2026-10-26): a seat with an active digital employee whose supervisor
//     changes must report to a seat held by a person or a digital employee, and the chain above
//     it must end at a person (`chainEndsAtPerson`; else `supervisor_required`);
//   - `authority` policy: approvals are decided by persons (05 §approval), so a new or
//     re-policied `authority` edge may not start at a seat held by a digital employee alone
//     (`supervisor_required`); `max_depth` is a delegation limit and is refused on it;
//   - `delegation` policy: `max_depth` (default 2 when absent, 05 §Delegation rule 6),
//     `budget_micros`, `allowed_task_kinds`; loops are the cycle rule (rule 6: no delegating
//     back into the chain);
//   - `reports_to` carries no policy.

export const EDGE_KINDS = ["reports_to", "authority", "delegation"] as const;
export type EdgeKind = (typeof EDGE_KINDS)[number];

export type EdgePolicy = {
  max_depth?: number | undefined;
  budget_micros?: number | undefined;
  allowed_task_kinds?: string[] | undefined;
};

export type SeatStatus = "proposed" | "open" | "filled" | "paused" | "closed";

export type ChartSeat = {
  id: string;
  status: SeatStatus;
  supervisorSeatId: string | null;
  /** An active assignment of an active person membership. */
  person: boolean;
  /** An active digital-employee assignment. */
  digital: boolean;
};

export type StoredEdge = {
  id: string;
  from: string;
  to: string;
  kind: EdgeKind;
  policy: EdgePolicy;
};

export type EdgeInput = { from: string; to: string; kind: EdgeKind; policy?: EdgePolicy };

export type ProblemCode =
  | "cycle"
  | "duplicate_reports_to"
  | "supervisor_required"
  | "unknown_seat"
  | "seat_closed";

/** A reason the edges would be refused. `seatIds` name only seats of the company's chart. */
export type Problem = { code: ProblemCode; seatIds: string[]; message: string };

/** A malformed body (422 `validation_failed`, never a graph problem). */
export type InputError = { path: string; message: string };

export type SupervisorChange = { seatId: string; from: string | null; to: string | null };

export type EdgePlan = {
  kinds: EdgeKind[];
  inputErrors: InputError[];
  problems: Problem[];
  added: EdgeInput[];
  updated: { edge: StoredEdge; policy: EdgePolicy }[];
  removed: StoredEdge[];
  supervisorChanges: SupervisorChange[];
};

/** A policy without absent keys, in a fixed key order (for comparison and storage). */
export function normalPolicy(p: EdgePolicy | undefined): EdgePolicy {
  const out: EdgePolicy = {};
  if (p?.max_depth !== undefined) out.max_depth = p.max_depth;
  if (p?.budget_micros !== undefined) out.budget_micros = p.budget_micros;
  if (p?.allowed_task_kinds !== undefined) out.allowed_task_kinds = [...p.allowed_task_kinds];
  return out;
}

export const samePolicy = (a: EdgePolicy | undefined, b: EdgePolicy | undefined) =>
  JSON.stringify(normalPolicy(a)) === JSON.stringify(normalPolicy(b));

const keyOf = (e: { from: string; to: string; kind: EdgeKind }) => `${e.kind}:${e.from}>${e.to}`;

/**
 * Every cycle of a graph given as adjacency lists, each as the seats on it in edge order, once
 * per cycle (by its smallest seat). Iterative DFS with colours; a self-edge is a cycle of one.
 */
export function cyclesOf(next: ReadonlyMap<string, readonly string[]>): string[][] {
  const colour = new Map<string, 1 | 2>(); // 1 on the stack, 2 done
  const found = new Map<string, string[]>();
  for (const start of [...next.keys()].sort()) {
    if (colour.has(start)) continue;
    const path: string[] = [];
    const stack: { node: string; i: number }[] = [{ node: start, i: 0 }];
    colour.set(start, 1);
    path.push(start);
    while (stack.length > 0) {
      const top = stack[stack.length - 1] as { node: string; i: number };
      const outs = next.get(top.node) ?? [];
      if (top.i >= outs.length) {
        colour.set(top.node, 2);
        stack.pop();
        path.pop();
        continue;
      }
      const to = outs[top.i++] as string;
      const c = colour.get(to);
      if (c === 1) {
        const cycle = path.slice(path.indexOf(to));
        const first = [...cycle].sort()[0] as string;
        const at = cycle.indexOf(first);
        const canonical = [...cycle.slice(at), ...cycle.slice(0, at)];
        found.set(canonical.join(">"), canonical);
      } else if (c === undefined) {
        colour.set(to, 1);
        path.push(to);
        stack.push({ node: to, i: 0 });
      }
    }
  }
  return [...found.values()];
}

/**
 * The reporting rule (Andrei, 2026-10-26; replaces "the supervisor seat has an active person"):
 * a digital employee reports to another digital employee or to a person, and every chain ends at
 * a person. Walks up from `start` (the DE seat's supervisor seat) along `supervisorOf`: a seat
 * with an active person ends the chain (true); a seat held by an active DE alone passes it on to
 * its own supervisor; a missing line, an empty seat or a loop is false.
 */
export function chainEndsAtPerson(
  start: string | null,
  supervisorOf: (seatId: string) => string | null,
  holders: (seatId: string) => { person: boolean; digital: boolean } | undefined,
): boolean {
  const seen = new Set<string>();
  let at = start;
  while (at !== null) {
    if (seen.has(at)) return false;
    seen.add(at);
    const h = holders(at);
    if (h?.person) return true;
    if (!h?.digital) return false;
    at = supervisorOf(at);
  }
  return false;
}

/** Adjacency lists of edges (sorted, so results do not depend on input order). */
function adjacency(edges: Iterable<{ from: string; to: string }>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const e of edges) {
    out.set(e.from, [...(out.get(e.from) ?? []), e.to]);
    if (!out.has(e.to)) out.set(e.to, []);
  }
  for (const v of out.values()) v.sort();
  return out;
}

export function planEdges(
  seats: readonly ChartSeat[],
  stored: readonly StoredEdge[],
  body: { kinds?: readonly EdgeKind[] | undefined; edges: readonly EdgeInput[] },
): EdgePlan {
  const kinds = body.kinds ? EDGE_KINDS.filter((k) => body.kinds?.includes(k)) : [...EDGE_KINDS];
  const inScope = new Set<EdgeKind>(kinds);
  const byId = new Map(seats.map((s) => [s.id, s]));
  const visible = (id: string) => {
    const s = byId.get(id);
    return s !== undefined && s.status !== "proposed";
  };
  const closed = (id: string) => byId.get(id)?.status === "closed";
  const inputErrors: InputError[] = [];
  const problems: Problem[] = [];

  // 1. The body on its own: kinds, duplicates, policies by kind.
  const seen = new Set<string>();
  const wellFormed: { e: EdgeInput; i: number }[] = [];
  body.edges.forEach((e, i) => {
    const at = `edges.${i}`;
    if (!inScope.has(e.kind)) {
      inputErrors.push({ path: `${at}.kind`, message: "not one of the kinds being replaced" });
      return;
    }
    if (seen.has(keyOf(e))) {
      inputErrors.push({ path: at, message: "the same edge appears twice" });
      return;
    }
    seen.add(keyOf(e));
    const p = normalPolicy(e.policy);
    if (e.kind === "reports_to" && Object.keys(p).length > 0) {
      inputErrors.push({ path: `${at}.policy`, message: "a reports_to edge carries no policy" });
      return;
    }
    if (e.kind === "authority" && p.max_depth !== undefined) {
      inputErrors.push({
        path: `${at}.policy.max_depth`,
        message: "max_depth limits delegation; an authority edge cannot carry it",
      });
      return;
    }
    wellFormed.push({ e: { ...e, policy: p }, i });
  });

  // 2. Seats the body names must be in the chart. Unknown ids are never echoed (they may be
  //    another company's): the problem names the edge by its index.
  const valid: EdgeInput[] = [];
  const unknownAt: number[] = [];
  for (const { e, i } of wellFormed) {
    if (!visible(e.from) || !visible(e.to)) unknownAt.push(i);
    else valid.push(e);
  }
  if (unknownAt.length > 0)
    problems.push({
      code: "unknown_seat",
      seatIds: [],
      message: `edges ${unknownAt.join(", ")} name a seat that is not in this org chart`,
    });

  // 3. Which stored edges the body replaces. Edges touching a proposed seat, and a closed
  //    seat's reporting line, are outside the replacement.
  const frozen = (e: { from: string; to: string; kind: EdgeKind }) =>
    !visible(e.from) || !visible(e.to) || (e.kind === "reports_to" && closed(e.from));
  const replaced = stored.filter((e) => inScope.has(e.kind) && !frozen(e));
  const storedByKey = new Map(stored.map((e) => [keyOf(e), e]));
  const replacedByKey = new Map(replaced.map((e) => [keyOf(e), e]));

  // A closed seat's line: the same target is a no-op, another one is refused.
  const desired: EdgeInput[] = [];
  for (const e of valid) {
    if (e.kind === "reports_to" && closed(e.from)) {
      if ((byId.get(e.from) as ChartSeat).supervisorSeatId !== e.to)
        problems.push({
          code: "seat_closed",
          seatIds: [e.from],
          message: "a closed seat's reporting line cannot change",
        });
      continue;
    }
    desired.push(e);
  }
  const desiredByKey = new Map(desired.map((e) => [keyOf(e), e]));

  // 4. The diff against the stored rows.
  const added = desired.filter((e) => !replacedByKey.has(keyOf(e)) && !storedByKey.has(keyOf(e)));
  const updated = desired.flatMap((e) => {
    const s = replacedByKey.get(keyOf(e));
    return s && !samePolicy(s.policy, e.policy)
      ? [{ edge: s, policy: normalPolicy(e.policy) }]
      : [];
  });
  const removed = replaced.filter((e) => !desiredByKey.has(keyOf(e)));

  // A new or re-policied edge touching a closed seat. A `reports_to` row that only mirrors the
  // seat's own `supervisor_seat_id` is not a new line (the field is canonical, the row may just
  // be missing), so it may name a closed supervisor.
  const mirrored = (e: { from: string; to: string; kind: EdgeKind }) =>
    e.kind === "reports_to" && byId.get(e.from)?.supervisorSeatId === e.to;
  for (const e of [
    ...added.filter((x) => !mirrored(x)),
    ...updated.map((u) => ({ ...u.edge, policy: u.policy })),
  ]) {
    const at = [e.from, e.to].filter(closed);
    if (at.length > 0)
      problems.push({
        code: "seat_closed",
        seatIds: at,
        message: `a new or changed ${e.kind} edge cannot touch a closed seat`,
      });
  }

  // 5. Reporting lines: one per seat, then the supervisor changes they make.
  const supervisorChanges: SupervisorChange[] = [];
  if (inScope.has("reports_to")) {
    const lines = new Map<string, string[]>();
    for (const e of desired.filter((x) => x.kind === "reports_to"))
      lines.set(e.from, [...(lines.get(e.from) ?? []), e.to]);
    for (const [from, tos] of lines)
      if (tos.length > 1)
        problems.push({
          code: "duplicate_reports_to",
          seatIds: [from],
          message: "a seat reports to one seat at most",
        });
    for (const s of seats) {
      if (!visible(s.id) || closed(s.id)) continue;
      const to = lines.get(s.id)?.[0] ?? null;
      if (to !== s.supervisorSeatId)
        supervisorChanges.push({ seatId: s.id, from: s.supervisorSeatId, to });
    }
    // Supervisor rule (02 Seat invariants; 13 invariant 2; the reporting rule of 2026-10-26):
    // over the lines the write would leave, a changed DE seat's chain must end at a person. A
    // chain only passes through DE-held seats, so checking the changed DE seats covers every DE
    // whose chain the write touches.
    const after = new Map(supervisorChanges.map((c) => [c.seatId, c.to]));
    const supervisorOf = (id: string) =>
      after.has(id) ? (after.get(id) ?? null) : (byId.get(id)?.supervisorSeatId ?? null);
    for (const c of supervisorChanges) {
      const s = byId.get(c.seatId) as ChartSeat;
      if (!s.digital) continue;
      if (!chainEndsAtPerson(c.to, supervisorOf, (id) => byId.get(id)))
        problems.push({
          code: "supervisor_required",
          seatIds: c.to === null ? [c.seatId] : [c.seatId, c.to],
          message:
            c.to === null
              ? "a digital employee's seat must report to a seat held by a person or a digital employee"
              : "the new supervisor's reporting chain does not end at a person",
        });
    }
  }

  // 6. Authority starts at a person: a new or re-policied authority edge from a seat held by a
  //    digital employee alone is refused (approvals are decided by persons).
  for (const e of [...added, ...updated.map((u) => u.edge)].filter((x) => x.kind === "authority")) {
    const s = byId.get(e.from) as ChartSeat;
    if (s.digital && !s.person)
      problems.push({
        code: "supervisor_required",
        seatIds: [e.from],
        message: "approval authority belongs to a person; this seat is held by a digital employee",
      });
  }

  // 7. Acyclic per kind, over the graph the write would leave.
  for (const kind of kinds) {
    const result =
      kind === "reports_to"
        ? seats.flatMap((s) => {
            const to =
              visible(s.id) && !closed(s.id)
                ? (desired.find((e) => e.kind === kind && e.from === s.id)?.to ?? null)
                : s.supervisorSeatId;
            return to === null ? [] : [{ from: s.id, to }];
          })
        : [
            ...stored.filter((e) => e.kind === kind && frozen(e)),
            ...desired.filter((e) => e.kind === kind),
          ];
    for (const cycle of cyclesOf(adjacency(result)))
      problems.push({
        code: "cycle",
        seatIds: cycle,
        message:
          cycle.length === 1
            ? `a seat cannot have a ${kind} edge to itself`
            : `these ${kind} edges form a cycle`,
      });
  }

  return {
    kinds,
    inputErrors,
    problems,
    added: added.map((e) => ({ ...e, policy: normalPolicy(e.policy) })),
    updated,
    removed,
    supervisorChanges,
  };
}
